/**
 * Thin API client stub (WP24).
 * Real route methods land with WP25+; error envelope matches Spec §K.
 */

import { applyAccessJwtHeader } from "./accessJwt.js";
import { getApiBaseUrl } from "./config.js";

export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type ApiFetchOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Override base URL (tests). */
  baseUrl?: string;
  /** Inject fetch (tests). */
  fetchImpl?: typeof fetch;
  /** Explicit Access JWT (tests / callers that already resolved it). */
  accessJwt?: string | null;
  /** Override JWT loader (tests). */
  getAccessJwt?: () => Promise<string | null>;
};

function joinUrl(base: string, path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (!base) return normalized;
  return `${base}${normalized}`;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (!value || typeof value !== "object") return false;
  const err = (value as { error?: unknown }).error;
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: unknown }).code;
  const message = (err as { message?: unknown }).message;
  return typeof code === "string" && typeof message === "string";
}

/**
 * JSON fetch against the CaraGo Worker API.
 * Attaches Cf-Access-Jwt-Assertion so the Worker can resolve the Access user.
 */
export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const {
    body,
    baseUrl = getApiBaseUrl(),
    fetchImpl = fetch,
    headers: initHeaders,
    accessJwt,
    getAccessJwt,
    ...rest
  } = options;

  const headers = new Headers(initHeaders);
  if (body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  await applyAccessJwtHeader(headers, { accessJwt, getAccessJwt });

  const response = await fetchImpl(joinUrl(baseUrl, path), {
    ...rest,
    credentials: rest.credentials ?? "include",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      if (!response.ok) {
        throw new ApiClientError(
          response.status,
          "PROVIDER_ERROR",
          text.slice(0, 200) || response.statusText,
        );
      }
      throw new ApiClientError(
        response.status,
        "PROVIDER_ERROR",
        "API returned non-JSON response",
      );
    }
  }

  if (!response.ok) {
    if (isApiErrorBody(parsed)) {
      throw new ApiClientError(
        response.status,
        parsed.error.code,
        parsed.error.message,
        parsed.error.details,
      );
    }
    throw new ApiClientError(
      response.status,
      "PROVIDER_ERROR",
      `Request failed (${response.status})`,
    );
  }

  return parsed as T;
}

/** Stub: GET /v1/health */
export function getHealth(options?: ApiFetchOptions) {
  return apiFetch<{ ok: boolean; db?: string }>("/v1/health", {
    method: "GET",
    ...options,
  });
}

/** Stub: GET /v1/me */
export function getMe(options?: ApiFetchOptions) {
  return apiFetch<{
    id: string;
    email: string;
    display_name: string | null;
    role: string;
  }>("/v1/me", { method: "GET", ...options });
}
